import type { LegalDoc } from '../types';

// Transcribed 1:1 from "04 - Guias de la Comunidad.pdf" (MEC Vault/legal/app-legal).
export const comunidad: LegalDoc = {
  slug: '/comunidad',
  title: 'Guías de la Comunidad',
  version: '1.0',
  updated: 'Julio de 2026',
  entity: 'Mi Envío Cuba',
  seoDescription:
    'Guías de la Comunidad de Mi Envío Cuba: normas de respeto, honestidad y buen uso de la plataforma para clientes, comerciantes y agencias.',
  closing: 'Fin de las Guías de la Comunidad.',
  sections: [
    {
      id: 'seccion-1',
      number: '1',
      title: 'Nuestro compromiso',
      blocks: [
        {
          t: 'p',
          text: 'Mi Envío Cuba es una plataforma que conecta familias, comerciantes, agencias y trabajadores en el corredor Estados Unidos — Cuba. Queremos que la experiencia sea respetuosa, honesta y útil para todos los que participan. Estas Guías de la Comunidad describen el comportamiento que esperamos.',
        },
        {
          t: 'p',
          text: 'Al usar la Aplicación, usted acepta cumplir con estas Guías. Las Guías forman parte de los Términos de Servicio y se aplican por igual a clientes, comerciantes, agencias y personal operativo.',
        },
      ],
    },
    {
      id: 'seccion-2',
      number: '2',
      title: 'Lo que esperamos de usted',
      blocks: [
        { t: 'h3', text: '2.1 Respeto' },
        {
          t: 'ul',
          items: [
            'Trate a los demás usuarios, comerciantes, agencias, y personal de MEC con respeto en toda comunicación (mensajes, reseñas, valoraciones, comentarios, redes sociales relacionadas a la plataforma).',
            'No use lenguaje ofensivo, discriminatorio, amenazante, ni acosador.',
            'No ataque a personas por su origen, religión, orientación política, orientación sexual, género, edad, o cualquier otra característica.',
          ],
        },
        { t: 'h3', text: '2.2 Honestidad' },
        {
          t: 'ul',
          items: [
            'Proporcione información veraz al registrarse y al realizar transacciones (identidad, direcciones, contenido de envíos, valor declarado).',
            'Las reseñas y valoraciones deben ser **basadas en experiencias reales**. No publique reseñas falsas, ni positivas ni negativas.',
            'Los comerciantes no deben publicar productos que no ofrecen realmente, ni exagerar sus características.',
          ],
        },
        { t: 'h3', text: '2.3 Buen uso de la plataforma' },
        {
          t: 'ul',
          items: [
            'Use la plataforma para los fines previstos: enviar paquetes, realizar pedidos, comunicarse con los actores del corredor.',
            'No use la plataforma para promover servicios competidores, ni para captar clientes o comerciantes hacia fuera de la plataforma.',
            'No cree múltiples cuentas para eludir suspensiones, obtener promociones repetidas, o inflar valoraciones.',
          ],
        },
        { t: 'h3', text: '2.4 Reserva de la conversación fuera de la plataforma' },
        {
          t: 'p',
          text: 'Los comerciantes y agencias no deben solicitar al cliente que realice la transacción fuera de la plataforma (por ejemplo, “pague por Zelle directo”, “no lo ordene por la app, hablamos por WhatsApp”). Este comportamiento perjudica a la comunidad, elimina las protecciones que la plataforma ofrece, y es motivo de suspensión.',
        },
      ],
    },
    {
      id: 'seccion-3',
      number: '3',
      title: 'Lo que NO está permitido',
      blocks: [
        {
          t: 'p',
          text: 'Independientemente de la severidad, los siguientes comportamientos pueden resultar en advertencia, suspensión temporal, o cierre permanente de la cuenta:',
        },
        { t: 'h3', text: '3.1 Contenido prohibido en reseñas, mensajes, y perfiles' },
        {
          t: 'ul',
          items: [
            'Discurso de odio, discriminación, incitación a la violencia.',
            'Amenazas o intimidación a otros usuarios.',
            'Contenido sexualmente explícito o pornográfico.',
            'Información personal privada de terceros sin su consentimiento (doxxing).',
            'Contenido que infrinja derechos de propiedad intelectual (fotos robadas, marcas ajenas, música con derechos de autor).',
            'Enlaces a estafas, malware, o esquemas fraudulentos.',
            'Spam, publicidad no autorizada, contenido repetitivo.',
          ],
        },
        { t: 'h3', text: '3.2 Conducta comercial prohibida' },
        {
          t: 'ul',
          items: [
            'Publicación de artículos prohibidos según nuestra Política de Artículos Prohibidos.',
            'Publicidad engañosa (fotos que no corresponden al producto real, promesas de entrega imposibles, precios ocultos).',
            'Manipulación de reseñas: pedir reseñas a cambio de descuentos, publicar reseñas falsas de la propia tienda, coordinar reseñas negativas contra un competidor.',
            'Cobrar cargos ocultos no divulgados en la aplicación.',
            'Rechazar reembolsos aplicables o cambiar términos después de recibir el pago.',
          ],
        },
        { t: 'h3', text: '3.3 Comportamiento contra la plataforma' },
        {
          t: 'ul',
          items: [
            'Intento de eludir sistemas de seguridad, verificación de identidad, filtrado OFAC, o límites de transacción.',
            'Uso de bots, scrapers, u otros medios automatizados para acceder a la plataforma sin autorización.',
            'Ingeniería inversa de la aplicación o intento de replicar el servicio.',
            'Presentar disputas de cargo (chargebacks) sin fundamento válido.',
            'Suplantación de identidad de otro usuario, un comerciante, una agencia, o de MEC.',
          ],
        },
      ],
    },
    {
      id: 'seccion-4',
      number: '4',
      title: 'Sistema de reseñas y valoraciones',
      blocks: [
        {
          t: 'p',
          text: 'Las reseñas y valoraciones son un pilar de confianza en la plataforma. Aplican las siguientes reglas:',
        },
        {
          t: 'ul',
          items: [
            'Las reseñas deben ser **basadas en experiencias reales** con el producto o servicio.',
            'Los comerciantes pueden responder a las reseñas, respetando las reglas de contenido de esta Guía.',
            'MEC **no elimina reseñas negativas solo por ser negativas**. MEC sí elimina reseñas que violen estas Guías (lenguaje ofensivo, información falsa demostrable, contenido irrelevante, doxxing, etc.).',
            'Los usuarios pueden **reportar** una reseña que consideren inapropiada mediante el flujo de reporte dentro de la Aplicación.',
            'La **manipulación de reseñas** (reseñas falsas, coordinadas, o compradas) resulta en suspensión de la cuenta involucrada.',
          ],
        },
      ],
    },
    {
      id: 'seccion-5',
      number: '5',
      title: 'Menciones de la marca y contenido generado por el usuario',
      blocks: [
        {
          t: 'ul',
          items: [
            'Si publica contenido en la Aplicación (fotos, comentarios, valoraciones, notas), usted otorga a MEC una licencia limitada, gratuita y mundial para usar dicho contenido en la Aplicación y en materiales relacionados con la plataforma. Conserva todos los derechos sobre su contenido original.',
            'Si usa la marca “Mi Envío Cuba” o marcas relacionadas en redes sociales, blogs, u otros medios, hágalo de manera veraz y respetuosa. No sugiera afiliación o patrocinio de MEC sin autorización expresa.',
          ],
        },
      ],
    },
    {
      id: 'seccion-6',
      number: '6',
      title: 'Consecuencias',
      blocks: [
        {
          t: 'p',
          text: 'Cuando MEC identifica una infracción de estas Guías, la respuesta depende de la severidad y del historial del usuario:',
        },
        { t: 'h3', text: '6.1 Advertencia' },
        {
          t: 'p',
          text: 'Para infracciones menores o primeras infracciones que no involucren daño demostrable: notificación al usuario, aclaración de la regla, y solicitud de rectificación.',
        },
        { t: 'h3', text: '6.2 Suspensión temporal' },
        {
          t: 'p',
          text: 'Para infracciones repetidas, o infracciones únicas de severidad media: suspensión de la cuenta por un plazo de 7, 14 o 30 días, según el caso. La cuenta puede reactivarse tras el plazo si el usuario reconoce y corrige el comportamiento.',
        },
        { t: 'h3', text: '6.3 Suspensión permanente' },
        {
          t: 'p',
          text: 'Para infracciones graves, patrones de mala conducta, o violaciones de la ley aplicable: cierre permanente de la cuenta, pérdida del acceso a la plataforma y a los datos asociados (sujeto a las obligaciones legales de retención), y prohibición de crear cuentas nuevas.',
        },
        { t: 'h3', text: '6.4 Reporte a autoridades' },
        {
          t: 'p',
          text: 'Para conductas que impliquen posibles delitos (fraude, lavado de dinero, tráfico de bienes prohibidos, violación de sanciones), MEC reportará a las autoridades competentes conforme a la ley aplicable.',
        },
      ],
    },
    {
      id: 'seccion-7',
      number: '7',
      title: 'Cómo reportar',
      blocks: [
        {
          t: 'p',
          text: 'Si observa un contenido, comportamiento, o cuenta que infringe estas Guías, reporte dentro de la Aplicación mediante el flujo de “Reportar” disponible en cada perfil, reseña, o transacción. También puede escribir a:',
        },
        { t: 'ul', items: ['**Correo:** support@mienviocuba.com'] },
        {
          t: 'p',
          text: 'Trate cada reporte con veracidad. Los reportes falsos o coordinados también son una infracción.',
        },
      ],
    },
    {
      id: 'seccion-8',
      number: '8',
      title: 'Apelación',
      blocks: [
        {
          t: 'p',
          text: 'Si su cuenta ha sido suspendida o cerrada y considera que la decisión fue errónea, puede apelar escribiendo a **support@mienviocuba.com**.',
        },
        {
          t: 'p',
          text: 'Incluya su nombre, correo asociado a la cuenta, y una explicación del caso. MEC responderá dentro de un plazo razonable, típicamente 7 a 14 días hábiles.',
        },
      ],
    },
    {
      id: 'seccion-9',
      number: '9',
      title: 'Modificaciones',
      blocks: [
        {
          t: 'p',
          text: 'MEC puede modificar estas Guías en cualquier momento. Los cambios materiales se notifican dentro de la Aplicación. El uso continuado tras la entrada en vigor de una modificación constituye aceptación.',
        },
        {
          t: 'p',
          text: 'Estas Guías forman parte integrante de los Términos de Servicio (v1.0) y se rigen por el mismo derecho aplicable y foro descrito en aquellos.',
        },
      ],
    },
  ],
};
